import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, User, Mail, Calendar, Coins, MessageSquare, Activity, Key, Eye, EyeOff, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CreditAuditCard } from '../components/admin/CreditAuditCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_LABELS = {
  stripe_purchase: { label: 'Stripe 充值', cls: 'bg-green-100 text-green-700' },
  admin_adjustment: { label: '管理員調整', cls: 'bg-blue-100 text-blue-700' },
  credit_expired: { label: '點數到期扣除', cls: 'bg-red-100 text-red-700' },
  ledger_reconciliation: { label: '稽核對帳（歷史未記錄扣除）', cls: 'bg-orange-100 text-orange-700' },
  auto_refund: { label: '自動退還', cls: 'bg-amber-100 text-amber-700' },
};

const actionLabel = (type) =>
  ACTION_LABELS[type] || { label: type?.startsWith('agent_') ? `對話 · ${type.replace(/^agent_/, '')}` : type, cls: 'bg-neutral-100 text-neutral-700' };

const AdminUserDetail = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [userDetail, setUserDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [updatingRole, setUpdatingRole] = useState(false);
  const [formConfig, setFormConfig] = useState([]);

  useEffect(() => {
    fetchUserDetail();
    fetchFormConfig();
  }, [userId]);

  const fetchUserDetail = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}`);
      setUserDetail(response.data);
    } catch (error) {
      toast.error('獲取用戶詳情失敗');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const fetchFormConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/form-config`);
      setFormConfig(response.data.fields || []);
    } catch (error) {
      console.error('獲取註冊表單配置失敗:', error);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('密碼長度至少6個字符');
      return;
    }
    
    setResettingPassword(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/reset-password`, {
        new_password: newPassword
      });
      toast.success('密碼已重設');
      setShowPasswordForm(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || '重設密碼失敗');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleRoleChange = async (newRole) => {
    if (!window.confirm(`確定要將此用戶角色改為「${getRoleLabel(newRole)}」嗎？`)) return;
    
    setUpdatingRole(true);
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/role`, {
        role: newRole
      });
      toast.success('用戶角色已更新');
      fetchUserDetail();
    } catch (error) {
      toast.error('更新失敗');
    } finally {
      setUpdatingRole(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      user: '普通用戶',
      vip: 'VIP 用戶',
      operator: '營運者',
      admin: '管理員'
    };
    return labels[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      user: 'bg-neutral-100 text-neutral-700',
      vip: 'bg-amber-100 text-amber-700',
      operator: 'bg-blue-100 text-blue-700',
      admin: 'bg-purple-100 text-purple-700'
    };
    return colors[role] || 'bg-neutral-100 text-neutral-700';
  };

  if (loading || !userDetail) {
    return (
      <div className="flex-1 flex items-center justify-center" data-testid="admin-user-detail-loading">
        <div className="text-atelier-ink">載入中...</div>
      </div>
    );
  }

  const { user, profile, credit_logs, conversation_count } = userDetail;

  const configFieldMap = formConfig.reduce((acc, field) => {
    acc[field.id] = field;
    return acc;
  }, {});

  const orderedProfileFields = formConfig
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((field) => ({
      key: field.id,
      label: field.label,
      value: profile?.[field.id]
    }));

  const extraProfileEntries = Object.entries(profile || {})
    .filter(([key]) => !['user_id', 'updated_at'].includes(key))
    .filter(([key]) => !configFieldMap[key])
    .map(([key, value]) => ({ key, label: key, value }));

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700 border-amber-300',
      approved: 'bg-green-100 text-green-700 border-green-300',
      rejected: 'bg-red-100 text-red-700 border-red-300',
      disabled: 'bg-neutral-200 text-neutral-600 border-neutral-400'
    };
    const labels = {
      pending: '待審批',
      approved: '已批准',
      rejected: '已拒絕',
      disabled: '已停用'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto" data-testid="admin-user-detail-page">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/users')}
            className="rounded-xl text-atelier-ink hover:text-white hover:bg-white/5"
            data-testid="admin-user-detail-back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            返回
          </Button>
          <h1 className="font-display text-3xl font-semibold text-atelier-ink tracking-tight" data-testid="admin-user-detail-title">
            用戶詳情
          </h1>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Left Column - User Info */}
          <div className="col-span-1 space-y-6">
            {/* Basic Info Card */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-neutral-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                  {user.name[0].toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-semibold text-neutral-800" data-testid="admin-user-detail-name">{user.name}</h2>
                    <span data-testid="admin-user-detail-status">{getStatusBadge(user.status)}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`} data-testid="admin-user-detail-role">
                      {getRoleLabel(user.role)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-neutral-400 mt-0.5" strokeWidth={2} />
                  <div>
                    <p className="text-xs text-neutral-500">郵箱</p>
                    <p className="text-sm font-medium text-neutral-800" data-testid="admin-user-detail-email">{user.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-neutral-400 mt-0.5" strokeWidth={2} />
                  <div>
                    <p className="text-xs text-neutral-500">註冊時間</p>
                    <p className="text-sm font-medium text-neutral-800" data-testid="admin-user-detail-created-at">
                      {new Date(user.created_at).toLocaleString('zh-TW')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Coins className="w-5 h-5 text-primary-500 mt-0.5" strokeWidth={2} />
                  <div>
                    <p className="text-xs text-neutral-500">點數餘額</p>
                    <p className="text-2xl font-bold text-primary-600" data-testid="admin-user-detail-credits">{user.credits}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MessageSquare className="w-5 h-5 text-neutral-400 mt-0.5" strokeWidth={2} />
                  <div>
                    <p className="text-xs text-neutral-500">對話數量</p>
                    <p className="text-sm font-medium text-neutral-800" data-testid="admin-user-detail-conversation-count">{conversation_count} 個對話</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-neutral-400 mt-0.5" strokeWidth={2} />
                  <div className="flex-1">
                    <p className="text-xs text-neutral-500 mb-2">用戶角色</p>
                    <Select 
                      value={user.role} 
                      onValueChange={handleRoleChange}
                      disabled={updatingRole}
                      data-testid="admin-user-detail-role-select"
                    >
                      <SelectTrigger className="bg-white w-full" data-testid="admin-user-detail-role-trigger">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="user" className="bg-white hover:bg-neutral-50" data-testid="admin-user-detail-role-option-user">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor('user')}`}>
                            普通用戶
                          </span>
                        </SelectItem>
                        <SelectItem value="vip" className="bg-white hover:bg-amber-50" data-testid="admin-user-detail-role-option-vip">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor('vip')}`}>
                            VIP 用戶
                          </span>
                        </SelectItem>
                        <SelectItem value="operator" className="bg-white hover:bg-blue-50" data-testid="admin-user-detail-role-option-operator">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor('operator')}`}>
                            營運者
                          </span>
                        </SelectItem>
                        <SelectItem value="admin" className="bg-white hover:bg-purple-50" data-testid="admin-user-detail-role-option-admin">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor('admin')}`}>
                            管理員
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Reset Password Section */}
              <div className="mt-6 pt-6 border-t border-neutral-200">
                {!showPasswordForm ? (
                  <Button
                    onClick={() => setShowPasswordForm(true)}
                    variant="outline"
                    className="w-full rounded-xl"
                    data-testid="admin-user-detail-reset-password-toggle"
                  >
                    <Key className="w-4 h-4 mr-2" />
                    重設密碼
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-neutral-700">重設用戶密碼</p>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="輸入新密碼（至少6位）"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="pr-10"
                        data-testid="admin-user-detail-new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                        data-testid="admin-user-detail-toggle-password-visibility"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleResetPassword}
                        disabled={resettingPassword || !newPassword}
                        className="flex-1 rounded-xl bg-primary hover:bg-primary-700"
                        data-testid="admin-user-detail-confirm-reset-password"
                      >
                        {resettingPassword ? '處理中...' : '確認重設'}
                      </Button>
                      <Button
                        onClick={() => {
                          setShowPasswordForm(false);
                          setNewPassword('');
                        }}
                        variant="outline"
                        className="rounded-xl"
                        data-testid="admin-user-detail-cancel-reset-password"
                      >
                        取消
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* View Conversations Button */}
              <Button
                onClick={() => navigate(`/admin/users/${userId}/conversations`)}
                variant="outline"
                className="w-full rounded-xl mt-4"
                data-testid="admin-user-detail-view-conversations"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                查看對話記錄
              </Button>
            </div>

            {/* Profile Data */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-neutral-200" data-testid="admin-user-detail-profile-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-neutral-800" data-testid="admin-user-detail-profile-title">註冊表單填寫資訊</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin/form-config')}
                  className="rounded-xl"
                  data-testid="admin-user-detail-form-config-link"
                >
                  查看表單配置
                </Button>
              </div>
              {profile ? (
                <div className="space-y-3">
                  {[...orderedProfileFields, ...extraProfileEntries].map((entry) => (
                    <div key={entry.key} data-testid={`admin-user-detail-profile-item-${entry.key}`}>
                      <p className="text-xs text-neutral-500 mb-1">{entry.label}</p>
                      <p className="text-sm text-neutral-800">{entry.value || '-'}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-neutral-500" data-testid="admin-user-detail-profile-empty">尚未填寫註冊表單</div>
              )}
            </div>
          </div>

          {/* Right Column - Activity */}
          <div className="col-span-2">
            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-md p-5 border border-neutral-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Coins className="w-6 h-6 text-primary-600" strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500">總消耗點數</p>
                    <p className="text-xl font-bold text-neutral-800" data-testid="admin-user-detail-total-spent">
                      {credit_logs.filter(log => log.amount < 0).reduce((sum, log) => sum + Math.abs(log.amount), 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-5 border border-neutral-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Activity className="w-6 h-6 text-green-600" strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500">總充值點數</p>
                    <p className="text-xl font-bold text-neutral-800" data-testid="admin-user-detail-total-recharged">
                      {credit_logs.filter(log => log.amount > 0).reduce((sum, log) => sum + log.amount, 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-5 border border-neutral-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-purple-600" strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500">對話總數</p>
                    <p className="text-xl font-bold text-neutral-800" data-testid="admin-user-detail-total-conversations">{conversation_count}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Credit audit */}
            <CreditAuditCard userId={userId} onRepaired={fetchUserDetail} />

            {/* Credit Logs */}
            <div className="bg-white rounded-2xl shadow-xl border border-neutral-200">
              <div className="p-6 border-b border-neutral-200">
                <h3 className="text-lg font-semibold text-neutral-800" data-testid="admin-user-detail-credit-log-title">點數使用記錄</h3>
              </div>
              <div className="divide-y divide-neutral-100">
                {credit_logs.length === 0 ? (
                  <div className="p-12 text-center text-neutral-500" data-testid="admin-user-detail-credit-log-empty">尚無使用記錄</div>
                ) : (
                  credit_logs.slice(0, 20).map(log => {
                    const meta = actionLabel(log.action_type);
                    return (
                    <div key={log.id} className={`p-4 hover:bg-neutral-50 transition-colors ${log.action_type === 'credit_expired' ? 'bg-red-50/60' : ''}`} data-testid={`admin-user-detail-credit-log-${log.id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${meta.cls}`} data-testid={`admin-user-detail-credit-log-type-${log.id}`}>{meta.label}</span>
                          {log.note && <p className="text-xs text-neutral-500 mt-1">{log.note}</p>}
                          <p className="text-xs text-neutral-400 mt-1">
                            {new Date(log.created_at).toLocaleString('zh-TW')}
                          </p>
                        </div>
                        <div className={`text-lg font-bold ${log.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {log.amount > 0 ? '+' : ''}{Number(log.amount).toFixed(2)}
                        </div>
                      </div>
                    </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUserDetail;
